import {
    buildUrl,
    formatZodValidationError,
    RegisteredWebSocketEndpoint,
    WebSocketEndpointOptions,
    webSocketEndpointRegistry,
    WebSocketFullEndpointOptions,
    WebSocketMessageHandler,
} from '@luminovo/api-contracts';
import { isProductionEnvironment } from '@luminovo/commons';
import * as Sentry from '@sentry/core';

import { resolveWebsocketBase } from '../../config';
function composeErrorMessage({
    message,
    endpoint,
    url,
    errorMessage,
    responseBody,
}: {
    message: string;
    endpoint: RegisteredWebSocketEndpoint;
    url: string;
    errorMessage: string;
    responseBody?: unknown;
}) {
    const endpointDef = webSocketEndpointRegistry[endpoint];
    return `${message}

Endpoint      : ${endpoint}
Description   : ${endpointDef.description}


Error message :
${errorMessage}

Here's the relevant information:

URL           : ${url}
Response Body :
${responseBody === undefined ? 'None' : JSON.stringify(responseBody, null, 2)}
`;
}

// For now we only support receiving messages, not sending them
export function generateWebSocket<T extends RegisteredWebSocketEndpoint>({
    endpoint,
    options,
    onMessage,
    onError,
    onClose,
}: {
    endpoint: T;
    options: WebSocketEndpointOptions<T>;
    onMessage: WebSocketMessageHandler<T>;
    onClose?: () => void;
    onError?: () => void;
}): WebSocket {
    const unsafeOptions = options as WebSocketFullEndpointOptions<T>;

    const { url } = buildUrl({
        endpoint,
        rootUrl: resolveWebsocketBase(),
        pathParams: unsafeOptions.pathParams ?? {},
        queryParams: unsafeOptions.queryParams ?? {},
    });

    const onMessageHandler = makeWebSocketMessageHandler(endpoint, onMessage);
    const connection = new WebSocket(url);

    connection.onmessage = onMessageHandler;
    connection.onclose = () => {
        if (onClose) {
            onClose();
        }
    };

    connection.onerror = () => {
        // Logged, not thrown: the event has no reason and a throw here goes uncaught; a per-endpoint rate helps more.
        Sentry.logger.warn(Sentry.logger.fmt`ws.error endpoint=${endpoint}`, { endpoint });

        if (onError) {
            onError();
        }
    };

    return connection;
}

/** Validates each message against the endpoint's response schema before calling the handler. */
export function makeWebSocketMessageHandler<T extends RegisteredWebSocketEndpoint>(
    endpoint: T,
    messageHandler: WebSocketMessageHandler<T>,
): (event: MessageEvent<string>) => void {
    return (unsafeEvent: MessageEvent<string>) => {
        try {
            const message = JSON.parse(unsafeEvent.data);

            if (!isProductionEnvironment()) {
                webSocketEndpointRegistry[endpoint].responseMessage.parse(message);
            }

            messageHandler(message);
        } catch (e) {
            const errorMessage = composeErrorMessage({
                message: 'Schema validation failed!',
                endpoint,
                url: '',
                errorMessage: formatZodValidationError(e, { format: 'pretty' }),
                responseBody: unsafeEvent.data,
            });

            throw new Error(errorMessage);
        }
    };
}

// Waits up to 300 ms for a connecting socket's handshake: closing it earlier fires `error` and then `close`.
export function closeWsConnection(ws: WebSocket, retriesLeft: number = 3): Promise<void> {
    return new Promise((resolve) => {
        if (ws.readyState === WebSocket.CONNECTING && retriesLeft > 0) {
            setTimeout(() => resolve(closeWsConnection(ws, retriesLeft - 1)), 100);
        } else {
            ws.close();
            resolve();
        }
    });
}

/** Closes a socket from `generateWebSocket` without its handlers, so a deliberate close isn't reported as a drop. */
export function detachAndCloseWsConnection(ws: WebSocket): Promise<void> {
    // `generateWebSocket` attaches its handlers through these properties, not `addEventListener`.
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    return closeWsConnection(ws);
}
